import React, { useState, useCallback, useEffect } from 'react';
import { createTheme, ThemeProvider, Container, Grid, Paper, Typography, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ListeDemandesLegrand } from './ListeDemandesLegrand';
import InfoVoyageurLegrand from './InfoVoyageurLegrand';
import InfoFormulaireLegrand from './InfoFormulaireLegrand';
import LegrandAppBar from './LegrandAppBar';

// Cr\u00e9ez un th\u00e8me personnalis\u00e9 avec les r\u00e9glages souhait\u00e9s pour le container
const theme = createTheme({
  components: {
    MuiContainer: {
      styleOverrides: {
        maxWidthLg: {
          '@media (min-width: 1200px)': {
            maxWidth: '2400px',
          },
        },
      },
    },
  },
});

// D\u00e9finition du type pour g\u00e9rer la s\u00e9lection
type SelectionType = {
  idVoyageur: number | null;
  chemin: string | null;
  pays: string | null;
  idDemande: number | null;
  email: string | null; // mail du compte client, affiché dans Informations du formulaire
};

const DemandesScreenLegrand: React.FC = () => {
  // Initialisation de la s\u00e9lection
  const [selection, setSelection] = useState<SelectionType>({
    idVoyageur: null,
    chemin: null,
    pays: null,
    idDemande: null,
    email: null,
  });

  const [key, setKey] = useState<number>(0); // Cl\u00e9 pour forcer le rechargement du composant ListeDemandes
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  // \u00c9tats pour g\u00e9rer l'expansion/collapse des sections
  const [isDemandesExpanded, setIsDemandesExpanded] = useState<boolean>(true);
  const [isVoyageurExpanded, setIsVoyageurExpanded] = useState<boolean>(true);
  const [isFormulaireExpanded, setIsFormulaireExpanded] = useState<boolean>(true);
  const [isTableCollapsed, setIsTableCollapsed] = useState<boolean>(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user_legrand');
    if (!storedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  /**
   * Fonction appel\u00e9e lorsque l'on clique sur une ligne dans ListeDemandes
   * @param idVoyageur L'identifiant du voyageur
   * @param chemin     Le chemin (dossier) associ\u00e9 \u00e0 la demande
   * @param pays       Le libell\u00e9 de la colonne Pays (demande.titre)
   * @param idDemande  L'identifiant de la demande
   */
  const handleSelectionDemande = (idVoyageur: number, chemin: string, pays: string, idDemande: number, email?: string) => {
    setSelection({ idVoyageur, chemin, pays, idDemande, email: email || null });
  };

  // Fonction pour rafra\u00eechir la liste des demandes
  const rafraichirDemandes = useCallback(() => {
    setKey((prevKey) => prevKey + 1);
  }, []);

  // Fonctions pour basculer l'expansion des sections
  const toggleDemandesExpansion = () => {
    setIsDemandesExpanded((prev) => !prev);
  };

  const toggleVoyageurExpansion = () => {
    setIsVoyageurExpanded((prev) => !prev);
  };

  const toggleFormulaireExpansion = () => {
    setIsFormulaireExpanded((prev) => !prev);
  };

  // Fonction pour gérer le changement d'état collapsed du tableau
  const handleCollapsedChange = (isCollapsed: boolean) => {
    setIsTableCollapsed(isCollapsed);
    if (isCollapsed && user?.role === 'superadmin') {
      // Si on collapse, on affiche automatiquement les sections voyageur et formulaire
      setIsVoyageurExpanded(true);
      setIsFormulaireExpanded(true);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <LegrandAppBar
        title="LEGRAND - Gestion des Demandes"
        username={user?.username}
      />

      <Container sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          {/* Liste des demandes avec possibilit\u00e9 de collapse */} 
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <IconButton onClick={toggleDemandesExpansion}>
                  {isDemandesExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>
              <Collapse in={isDemandesExpanded}>
                <ListeDemandesLegrand
                  onSelectionDemande={handleSelectionDemande}
                  onCollapsedChange={handleCollapsedChange}
                  key={key}
                />
              </Collapse>
            </Paper>
          </Grid>

          {/* Informations du Voyageur et Formulaire - Afficher si collapsed (superadmin) ou si voyageur sélectionné */}
          {(isTableCollapsed && user?.role === 'superadmin' && selection.idVoyageur) && (
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Informations du Voyageur</Typography>
                    <IconButton onClick={toggleVoyageurExpansion}>
                      {isVoyageurExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </div>
                  <Collapse in={isVoyageurExpanded}>
                    <InfoVoyageurLegrand
                      key={selection.idVoyageur ?? 0}
                      idVoyageur={selection.idVoyageur ?? 0}
                      chemin={selection.chemin ?? ""}
                      pays={selection.pays ?? ""}
                      idDemande={selection.idDemande ?? 0}
                      onValider={rafraichirDemandes}
                    />
                  </Collapse>
                </Paper>
              </Grid>

              {/* Informations du Formulaire */}
              <Grid item xs={12} md={6} lg={6}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Informations du Formulaire</Typography>
                    <IconButton onClick={toggleFormulaireExpansion}>
                      {isFormulaireExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </div>
                  <Collapse in={isFormulaireExpanded}>
                    <InfoFormulaireLegrand chemin={selection.chemin ?? ""} emailClient={selection.email} />
                  </Collapse>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Container>
    </ThemeProvider>
  );
};

export default DemandesScreenLegrand;